/**
 * SynFolio UI runtime — the single browser-side library responsible for
 * resolving `data-ui-*` context per the Instrumentation Pack §7.
 *
 * For now it provides:
 *   - getNode(el): read the immediate node's attributes
 *   - getContext(el): walk ancestors and merge entity context
 *   - install(): wire up autocapture so PostHog (or any other sink)
 *     receives an event ONLY for elements carrying data-ui-action
 *
 * Future tour/help consumers would use this same module.
 */

import { track } from './analytics';

export interface UiNodeContext {
  name: string;
  entity?: string;
  entityId?: string;
  action?: string;
  state?: string;
  label?: string;
}

export interface UiContext {
  node: UiNodeContext;
  ancestors: UiNodeContext[];
  route: string;
}

export function getNode(el: Element): UiNodeContext | null {
  const name = el.getAttribute('data-ui-name');
  if (!name) return null;
  const ctx: UiNodeContext = { name };
  const entity = el.getAttribute('data-ui-entity');
  const entityId = el.getAttribute('data-ui-entity-id');
  const action = el.getAttribute('data-ui-action');
  const state = el.getAttribute('data-ui-state');
  const label = el.getAttribute('data-ui-label');
  if (entity) ctx.entity = entity;
  if (entityId) ctx.entityId = entityId;
  if (action) ctx.action = action;
  if (state) ctx.state = state;
  if (label) ctx.label = label;
  return ctx;
}

export function getContext(el: Element): UiContext | null {
  const node = getNode(el);
  if (!node) return null;
  const ancestors: UiNodeContext[] = [];
  let cursor: Element | null = el.parentElement;
  while (cursor) {
    const n = getNode(cursor);
    if (n) ancestors.push(n);
    cursor = cursor.parentElement;
  }
  // Nearest-wins entity inheritance per pack §7.
  if (!node.entity) {
    for (const a of ancestors) {
      if (a.entity) {
        node.entity = a.entity;
        node.entityId = node.entityId ?? a.entityId;
        break;
      }
    }
  }
  return { node, ancestors, route: window.location.pathname };
}

/**
 * Install global click autocapture. Per pack §9, only elements carrying
 * `data-ui-action` are trackable. Anchor-only elements (just data-ui-name)
 * are addressable but not captured.
 */
export function installAutocapture(): void {
  if (typeof window === 'undefined') return;
  if ((window as { __synfolioUiInstalled?: boolean }).__synfolioUiInstalled) return;
  (window as { __synfolioUiInstalled?: boolean }).__synfolioUiInstalled = true;

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null;
      if (!target) return;
      const el = target.closest('[data-ui-name]');
      if (!el) return;
      const ctx = getContext(el);
      if (!ctx?.node.action) return; // anchor-only — not tracked
      track('ui_interaction', {
        event_type: 'click',
        ui_name: ctx.node.name,
        entity: ctx.node.entity,
        entity_id: ctx.node.entityId,
        action: ctx.node.action,
        state: ctx.node.state,
        label: ctx.node.label,
        route: ctx.route,
        ancestor_names: ctx.ancestors.map((a) => a.name),
      });
    },
    { capture: true },
  );
}
